/**
 * <MeListPage /> — Session 3d shared generic /me list page.
 *
 * Composes the locked Session 3d shell + Khidmat AI surfaces + a
 * DataTable-style listing. Most /me list screens compose from this
 * generic page; detail-card-style screens (Hub · Latest Payslip ·
 * EOS Projection · Profile · Bank · Today · Shift) use the shell
 * directly with custom bodies.
 *
 * Per Commandment 73: NO window.alert · NO 'Live wiring lands'
 * phrasing. Uses showAlertTrace for popovers, ComingSoonBadge for
 * unwired CTAs.
 *
 * Per Commandment 74: every visible string wrapped in t() with
 * an English fallback.
 */

import { Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, TraceableValue } from "../components/ui";
import { showAlertTrace } from "./_session3aHelpers";
import {
  MeActionsRow,
  MeEmployeeRequiredGate,
  MeEmptyState,
  Session3dMeShell,
} from "./_session3dShell";

export interface MeColumn<R> {
  headerKey: string;
  label: string;
  render: (row: R) => ReactNode;
  /** When set, render the value through <TraceableValue> with this trace text. */
  traceText?: (row: R) => string;
}

export interface MeListPageProps<R> {
  title: string;
  subtitle: string;
  /** Domain key — used for cross-screen navigation + brief context. */
  domain: string;
  /** Optional left-aligned hint above the table. */
  hint?: string;
  /** Optional + Request CTA label. Omit for read-only screens. */
  requestLabel?: string;
  onRequestClick?: () => void;
  /** Pre-filled question for the Ask Khidmat chat button. */
  khidmatPrefill: string;
  /** Data query. */
  query: UseQueryResult<{ items: R[] }, Error>;
  /** Column definitions. */
  columns: MeColumn<R>[];
  /** Row-key extractor. */
  rowKey: (row: R) => string;
  /** Optional 1-2 inline Khidmat suggestions above the table. */
  suggestions?: Array<{
    headline: string;
    body: string;
    severity?: "info" | "warning" | "critical";
    primaryActionLabel: string;
    primaryActionOnClick: () => void;
    suggestionKey: string;
  }>;
  /** Brief context for the right-rail Khidmat panel. */
  briefContext: {
    total: number;
    attention: number;
    critical: number;
    top_kind: string;
    window_label: string;
  };
  /** Custom empty state (overrides the default warm empty). */
  emptyHeadline?: string;
  emptyBody?: string;
}

export function MeListPage<R>(props: MeListPageProps<R>) {
  const { t } = useTranslation();
  const {
    title, subtitle, domain,
    hint, requestLabel, onRequestClick, khidmatPrefill,
    query, columns, rowKey,
    suggestions = [],
    briefContext,
    emptyHeadline, emptyBody,
  } = props;

  const items: R[] = query.data?.items ?? [];

  return (
    <MeEmployeeRequiredGate>
      <Session3dMeShell
        title={title}
        subtitle={subtitle}
        briefContext={briefContext}
        actionsRow={
          <MeActionsRow
            hint={hint}
            requestLabel={requestLabel}
            onRequestClick={onRequestClick}
            khidmatPrefill={khidmatPrefill}
          />
        }
      >
        {suggestions.map((s) => (
          <InlineAISuggestion
            key={s.suggestionKey}
            agent="khidmat"
            icon={<Sparkles className="h-4 w-4" />}
            severity={s.severity ?? "info"}
            headline={s.headline}
            body={s.body}
            primaryAction={{
              label: s.primaryActionLabel,
              onClick: s.primaryActionOnClick,
            }}
            suggestionKey={s.suggestionKey}
          />
        ))}

        {query.isLoading && <Loading />}
        {query.isError && <ErrorPanel message={(query.error as Error).message} />}

        {!query.isLoading && items.length === 0 && (
          <MeEmptyState
            headline={emptyHeadline ?? t(`me.${domain}.empty_headline`, "Nothing here yet.")}
            body={emptyBody ?? t(`me.${domain}.empty_body`, "Khidmat is ready when you need it.")}
            khidmatPrefill={khidmatPrefill}
          />
        )}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  {columns.map((c) => (
                    <th key={c.headerKey} className="px-3 py-2">
                      {t(c.headerKey, c.label)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={rowKey(row)}
                    className="group border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    {columns.map((c) => {
                      const tracer = c.traceText?.(row);
                      if (tracer) {
                        return (
                          <td key={c.headerKey} className="px-3 py-2">
                            <TraceableValue onTraceClick={() => showAlertTrace(tracer)}>
                              {c.render(row)}
                            </TraceableValue>
                          </td>
                        );
                      }
                      return (
                        <td key={c.headerKey} className="px-3 py-2">
                          {c.render(row)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Session3dMeShell>
    </MeEmployeeRequiredGate>
  );
}
